import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dsoApi } from '../../services/api';
import { FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import './RequestMyPayoutDetails.css';

function DSORequestMyPayoutDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [agents, setAgents] = useState([]);
  const [pendingCases, setPendingCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  // Fetch agents and their pending cases
  useEffect(() => {
    const fetchAgentsAndCases = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch DSO agents
        const agentsResponse = await dsoApi.getMyAgents({ limit: 100 });
        const agentsList = agentsResponse.data.items || [];
        setAgents(agentsList);

        // Fetch pending cases that can be included in a payout
        const pendingCasesResponse = await dsoApi.getEligibleCasesForPayout();
        // Ensure pendingCases is an array
        setPendingCases(Array.isArray(pendingCasesResponse.data) 
          ? pendingCasesResponse.data 
          : pendingCasesResponse.data?.items || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load agents and cases data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAgentsAndCases();
  }, []);

  // Calculate total amount when selected cases change
  useEffect(() => {
    const total = selectedCases.reduce((sum, caseId) => {
      const foundCase = pendingCases.find(c => c.id === caseId);
      // Handle potential different field names for the commission amount
      const commissionAmount = foundCase ? 
        (foundCase.payout_amount || foundCase.commission_amount || 0) : 0;
      return sum + commissionAmount;
    }, 0);
    setTotalAmount(total);
  }, [selectedCases, pendingCases]);

  // Handle checkbox change for case selection
  const handleCaseSelection = (caseId) => {
    if (selectedCases.includes(caseId)) {
      setSelectedCases(selectedCases.filter(id => id !== caseId));
    } else {
      setSelectedCases([...selectedCases, caseId]);
    }
  };

  // Handle select all cases for an agent
  const handleSelectAllForAgent = (agentId, isSelected) => {
    const agentCaseIds = pendingCases
      .filter(c => c.agent_id === agentId)
      .map(c => c.id);

    if (isSelected) {
      // Add all agent's cases that aren't already selected
      const newSelectedCases = [...selectedCases];
      agentCaseIds.forEach(caseId => {
        if (!newSelectedCases.includes(caseId)) {
          newSelectedCases.push(caseId);
        }
      });
      setSelectedCases(newSelectedCases);
    } else {
      // Remove all agent's cases
      setSelectedCases(selectedCases.filter(id => !agentCaseIds.includes(id)));
    }
  };

  // Check if all cases for an agent are selected
  const areAllAgentCasesSelected = (agentId) => {
    const agentCaseIds = pendingCases
      .filter(c => c.agent_id === agentId)
      .map(c => c.id);

    return agentCaseIds.length > 0 && 
      agentCaseIds.every(caseId => selectedCases.includes(caseId));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedCases.length === 0) {
      setError('Please select at least one case for payout');
      return;
    }

    setSubmitting(true);
    setError('');
    
    try {
      await dsoApi.requestPayout({
        case_ids: selectedCases
      });
      
      setSuccess(true);
      
      // Redirect after a brief delay to show success message
      setTimeout(() => {
        navigate('/dso/payouts');
      }, 2000);
    } catch (err) {
      console.error('Error requesting payout:', err);
      setError('Failed to submit payout request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Group cases by agent
  const casesByAgent = {};
  if (Array.isArray(pendingCases)) {
    pendingCases.forEach(caseItem => {
      if (caseItem && caseItem.agent_id !== undefined) {
        if (!casesByAgent[caseItem.agent_id]) {
          casesByAgent[caseItem.agent_id] = [];
        }
        casesByAgent[caseItem.agent_id].push(caseItem);
      }
    });
  }

  // Get agent name by ID
  const getAgentName = (agentId) => {
    if (!agentId) return 'Unknown Agent';
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : 'Unknown Agent';
  };

  return (
    <div className="dso-payout-request-page">
      <div className="dso-payout-header">
        <h2>Request Payout</h2>
        <button 
          className="dso-back-button"
          onClick={() => navigate('/dso/payouts')}
        >
          Back to Payouts
        </button>
      </div>

      {loading && (
        <div className="dso-loading-state">
          <FaSpinner className="dso-spinner" />
          <span>Loading cases data...</span>
        </div>
      )}

      {error && (
        <div className="dso-error-state">
          <FaExclamationTriangle className="dso-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="dso-success-state">
          <FaCheck className="dso-success-icon" />
          <span>Payout request submitted successfully! Redirecting...</span>
        </div>
      )}

      {!loading && !success && pendingCases.length === 0 && (
        <div className="dso-no-cases">
          <p>No eligible cases found for payout request.</p>
          <p>Cases are eligible once they have been approved but not yet included in a payout.</p>
        </div>
      )}

      {!loading && !success && pendingCases.length > 0 && (
        <form onSubmit={handleSubmit} className="dso-payout-form">
          <div className="dso-summary-card">
            <div className="dso-summary-item">
              <span className="dso-summary-label">Available Cases:</span>
              <span className="dso-summary-value">{pendingCases.length}</span>
            </div>
            <div className="dso-summary-item">
              <span className="dso-summary-label">Selected Cases:</span>
              <span className="dso-summary-value">{selectedCases.length}</span>
            </div>
            <div className="dso-summary-item dso-summary-total">
              <span className="dso-summary-label">Total Amount:</span>
              <span className="dso-summary-value dso-amount">{formatCurrency(totalAmount)}</span>
            </div>
          </div>

          <div className="dso-case-groups">
            {Object.keys(casesByAgent).map(agentId => (
              <div key={agentId} className="dso-agent-case-group">
                <div className="dso-agent-header">
                  <div className="dso-agent-check">
                    <input
                      type="checkbox"
                      id={`agent-${agentId}`}
                      checked={areAllAgentCasesSelected(parseInt(agentId))}
                      onChange={(e) => handleSelectAllForAgent(parseInt(agentId), e.target.checked)}
                    />
                    <label htmlFor={`agent-${agentId}`} className="dso-agent-name">
                      {getAgentName(parseInt(agentId))}
                    </label>
                  </div>
                  <div className="dso-agent-total">
                    <span>Total: {casesByAgent[agentId].length} cases</span>
                  </div>
                </div>
                
                <div className="dso-case-list">
                  <table className="dso-cases-table">
                    <thead>
                      <tr>
                        <th className="dso-checkbox-col"></th>
                        <th>Case ID</th>
                        <th>Lead Name</th>
                        <th>Product</th>
                        <th>Application Number</th>
                        <th>Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {casesByAgent[agentId].map(caseItem => (
                        <tr key={caseItem.id}>
                          <td className="dso-checkbox-col">
                            <input
                              type="checkbox"
                              id={`case-${caseItem.id}`}
                              checked={selectedCases.includes(caseItem.id)}
                              onChange={() => handleCaseSelection(caseItem.id)}
                            />
                          </td>
                          <td>{caseItem.id}</td>
                          <td>{caseItem.lead_name || caseItem.name || 'N/A'}</td>
                          <td>{caseItem.product_name || caseItem.product || 'N/A'}</td>
                          <td>{caseItem.application_number || caseItem.application_no || 'N/A'}</td>
                          <td className="dso-amount">{formatCurrency(caseItem.payout_amount || caseItem.commission_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div className="dso-payout-actions">
            <button 
              type="button" 
              className="dso-cancel-button"
              onClick={() => navigate('/dso/payouts')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="dso-submit-button"
              disabled={submitting || selectedCases.length === 0}
            >
              {submitting ? (
                <>
                  <FaSpinner className="dso-spinner" />
                  Submitting...
                </>
              ) : (
                `Request Payout (${formatCurrency(totalAmount)})`
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default DSORequestMyPayoutDetails;